import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DollarSign, X, Check, Loader2, AlertTriangle } from 'lucide-react';

interface Employee {
  id: string;
  name: string;
  employeeId: string;
}

interface Calculation {
  totalAmount: number;
}

interface BankTransferDetails {
  accountNumber: string;
  bankName: string;
  transferReference: string;
}

interface PaymentData {
  paymentMethod: string;
  paymentDate: string;
  notes: string;
  referenceNumber: string;
  approvedBy: string;
  bankTransferDetails: BankTransferDetails;
}

interface PaymentRecord extends PaymentData {
  employeeId: string;
  employeeName: string;
  amount: number;
  currency: string;
  processedAt: string;
  processedBy: string;
  status: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  employee: Employee;
  calculation: Calculation;
  onPaymentRecorded: (record: PaymentRecord) => void;
}

export default function MarkAsPaidModal({ isOpen, onClose, employee, calculation, onPaymentRecorded }: Props) {
  const { t } = useTranslation();
  const [paymentData, setPaymentData] = useState<PaymentData>({
    paymentMethod: '',
    paymentDate: new Date().toISOString().split('T')[0],
    notes: '',
    referenceNumber: '',
    approvedBy: '',
    bankTransferDetails: {
      accountNumber: '',
      bankName: '',
      transferReference: '',
    },
  });

  const [isProcessing, setIsProcessing] = useState(false);

  const paymentMethods = [
    { value: 'bank_transfer', label: t('Bank Transfer') },
    { value: 'cash', label: t('Cash Payment') },
    { value: 'cheque', label: t('Cheque') },
    { value: 'wire_transfer', label: t('Wire Transfer') },
  ];

  const handleInputChange = (field: string, value: string) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setPaymentData((prev) => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof PaymentData] as object),
          [child]: value,
        },
      }));
    } else {
      setPaymentData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!paymentData.paymentMethod) {
      alert(t('Please select a payment method'));
      return;
    }

    if (!paymentData.paymentDate) {
      alert(t('Please select a payment date'));
      return;
    }

    setIsProcessing(true);

    setTimeout(() => {
      const paymentRecord: PaymentRecord = {
        ...paymentData,
        employeeId: employee.id,
        employeeName: employee.name,
        amount: calculation.totalAmount,
        currency: 'AED',
        processedAt: new Date().toISOString(),
        processedBy: 'current_user_id',
        status: 'completed',
      };

      onPaymentRecorded(paymentRecord);
      setIsProcessing(false);
      setPaymentData({
        paymentMethod: '',
        paymentDate: new Date().toISOString().split('T')[0],
        notes: '',
        referenceNumber: '',
        approvedBy: '',
        bankTransferDetails: {
          accountNumber: '',
          bankName: '',
          transferReference: '',
        },
      });
    }, 2000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-900">{t('Mark as Paid')}</h3>
              <p className="text-slate-600">{t('Record manual payment from finance')}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="bg-slate-50 rounded-xl p-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-slate-500 mb-1">{t('Employee')}</p>
                <p className="font-semibold text-slate-900">{employee.name}</p>
                <p className="text-sm text-slate-600">{employee.employeeId}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">{t('EOS Amount')}</p>
                <p className="text-2xl font-bold text-emerald-600">AED {calculation.totalAmount.toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              {t('Payment Method')} <span className="text-red-500">*</span>
            </label>
            <select
              value={paymentData.paymentMethod}
              onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-emerald-500"
            >
              <option value="">{t('Select payment method')}</option>
              {paymentMethods.map((method) => (
                <option key={method.value} value={method.value}>
                  {method.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              {t('Payment Date')} <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={paymentData.paymentDate}
              onChange={(e) => handleInputChange('paymentDate', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">{t('Reference Number')}</label>
            <input
              type="text"
              value={paymentData.referenceNumber}
              onChange={(e) => handleInputChange('referenceNumber', e.target.value)}
              placeholder={t('Transaction or reference number')}
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          {paymentData.paymentMethod === 'bank_transfer' && (
            <div className="space-y-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
              <h4 className="font-medium text-blue-900">{t('Bank Transfer Details')}</h4>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">{t('Bank Name')}</label>
                <input
                  type="text"
                  value={paymentData.bankTransferDetails.bankName}
                  onChange={(e) => handleInputChange('bankTransferDetails.bankName', e.target.value)}
                  placeholder={t('Enter bank name')}
                  className="w-full px-3 py-2 border border-border rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {t('Account Number (Last 4 digits)')}
                </label>
                <input
                  type="text"
                  value={paymentData.bankTransferDetails.accountNumber}
                  onChange={(e) => handleInputChange('bankTransferDetails.accountNumber', e.target.value)}
                  placeholder="****1234"
                  maxLength={4}
                  className="w-full px-3 py-2 border border-border rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">{t('Transfer Reference')}</label>
                <input
                  type="text"
                  value={paymentData.bankTransferDetails.transferReference}
                  onChange={(e) => handleInputChange('bankTransferDetails.transferReference', e.target.value)}
                  placeholder={t("Bank's transfer reference number")}
                  className="w-full px-3 py-2 border border-border rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">{t('Approved By')}</label>
            <input
              type="text"
              value={paymentData.approvedBy}
              onChange={(e) => handleInputChange('approvedBy', e.target.value)}
              placeholder={t('Finance manager or approver name')}
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">{t('Notes (Optional)')}</label>
            <textarea
              value={paymentData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder={t('Any additional notes about the payment...')}
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
            />
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">{t('Important Notice')}</p>
                <p className="text-sm text-amber-700 mt-1">
                  {t(
                    'After marking as paid, the calculator will be permanently locked and cannot be modified. Please ensure all payment details are correct before proceeding.'
                  )}
                </p>
              </div>
            </div>
          </div>

          <div className="flex space-x-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isProcessing}
              className="flex-1 px-4 py-2 border border-border rounded-lg hover:bg-slate-100 transition-colors disabled:opacity-50"
            >
              {t('Cancel')}
            </button>
            <button
              type="submit"
              disabled={isProcessing}
              className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{t('Recording Payment...')}</span>
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  <span>{t('Mark as Paid')}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
